<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" lg="10" offset-lg="1">
        <h2 class="title mb-3">監修者一覧</h2>
        <permission-guard situation="expert_management">
          <v-btn color="primary" dark tile @click="newExpert">
            新規監修者追加
          </v-btn>
          <v-simple-table fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center" style="width: 50px">ID</th>
                  <th class="text-left">アバター</th>
                  <th class="text-left">名前</th>
                  <th class="text-left">分野</th>
                  <th class="text-left">編集</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="expert in experts" :key="expert.id">
                  <td class="text-center">
                    {{ expert.id }}
                  </td>
                  <td>
                    <v-avatar size="40">
                      <img :src="expert.avatar.thumb.url" />
                    </v-avatar>
                  </td>
                  <td>
                    {{ expert.name }}
                  </td>
                  <td>{{ expert.position }}</td>
                  <td>
                    <v-btn
                      color="primary"
                      dark
                      tile
                      @click="editExpert(expert)"
                    >
                      編集
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </permission-guard>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title />
        <v-card-text>
          <v-form ref='userProfileForm'>
            <v-text-field
              v-model="expert.name"
              style="width: 300px"
              label="監修者名"
              :rules="[presenceValidation]"
            >
              <template v-slot:label>
                <InputLabel labelName="監修者名" :required="true" />
              </template>
            </v-text-field>
            <v-text-field
              v-model="expert.position"
              style="width: 300px"
              label="分野"
            />
            <v-textarea v-model="expert.profile" label="プロフィール" />
            <ImageFormAndThumbnail
              ref="imageFormAndThumbnail"
              label-name='アバター画像'
              :propsImagePreviewURL="expert.avatar ? expert.avatar.url : ''"
              @change-image-file="(value) => expert.avatar = value"
            ></ImageFormAndThumbnail>
            <profile-links-form
              :profileLinks="expert.profileLinks"
              :destroyProfileLinks="destroyProfileLinks"
              ref='profileLinksForm'
            />
          </v-form>
          <v-card-actions>
            <v-btn
              v-if="expert.id != null"
              color="green darken-1"
              text
              @click="updateExpert"
            >
              更新する
            </v-btn>
            <v-btn v-else color="green darken-1" text @click="createExpert">
              作成する
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import PermissionGuard from '@/components/commons/PermissionGuard'
import headUtilsMixin from '@/mixins/head_utils'
import ImageFormAndThumbnail from '@/components/commons/ImageFormAndThumbnail'
import ProfileLinksForm from '@/components/commons/ProfileLinksForm'
import snakecaseKeys from 'snakecase-keys'
import InputLabel from '@/components/commons/formParts/inputLabel'
import formValidationUtils from '@/mixins/form_validation_utils'

export default {
  components: {
    PermissionGuard,
    ImageFormAndThumbnail,
    ProfileLinksForm,
    InputLabel
  },
  filters: {
    moment: function (val) {
      if (val) {
        return moment(val).format('YYYY年M月D日 HH時mm分')
      }
    }
  },
  mixins: [headUtilsMixin, formValidationUtils],
  data: () => {
    return {
      pageTitle: '監修者一覧',
      page: 1,
      dialog: false,
      experts: [],
      expert: {
        id: null,
        name: null,
        position: null,
        profile: null,
        avatar: null,
        profileLinks: []
      },
      destroyProfileLinks: []
    }
  },
  mounted: function () {
    axios.get('/api/experts').then(
      ({ data }) => {
        data.data.forEach(({ attributes }) => this.experts.push(attributes))
      },
      (error) => {
        const payload = {
          status: error.response.status,
          message: error.response.data.errors
            ? error.response.data.errors.toString()
            : '空白です'
        }
        this.$store.dispatch('SystemMessageStore/createMessage', payload)
      }
    )
  },
  watch: {
    dialog (value) {
      if (value == false) {
        this.expert = {}
        this.$refs.imageFormAndThumbnail.imageFile = null
        this.$refs.imageFormAndThumbnail.imagePreviewURL = null
      }
    }
  },
  methods: {
    newExpert () {
      this.expert = {
        id: null,
        name: null,
        position: null,
        profile: null,
        avatar: null,
        profileLinks: []
      }
      this.dialog = true
    },
    editExpert (expert) {
      this.expert = { ...expert }
      this.dialog = true
    },
    updateExpert () {
      if (!this.$refs.userProfileForm.validate() || !this.$refs.profileLinksForm.validate()) return false

      const payload = this.expert
      const sendProfileLinks = this.expert.profileLinks.concat(
        this.destroyProfileLinks
      )
      let formData = new FormData()
      formData.append('expert[name]', payload.name)
      formData.append('expert[position]', payload.position)
      formData.append('expert[profile]', payload.profile)
      formData.append('expert[avatar]', payload.avatar)
      let arr = snakecaseKeys(sendProfileLinks)
      arr = arr.map(obj => {
        const {_destroy, ...rest} = obj
        return {_destroy: obj.destroy, ...rest}
      })
      formData.append('expert[profile_links_attributes]', JSON.stringify(arr))
      axios
        .patch(`/api/experts/${payload.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(
          ({ data }) => {
            const expert = data.data.attributes
            Object.assign(
              this.experts.find((e) => e.id === expert.id),
              expert
            )
            this.dialog = false
            const payload = {
              status: 200,
              message: '監修者情報を更新しました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            console.log(error)
          }
        )
    },
    createExpert () {
      if (!this.$refs.userProfileForm.validate() || !this.$refs.profileLinksForm.validate()) return false

      const payload = this.expert
      const sendProfileLinks = this.expert.profileLinks
      let formData = new FormData()
      formData.append('expert[name]', payload.name)
      formData.append('expert[position]', payload.position)
      formData.append('expert[profile]', payload.profile)
      formData.append('expert[avatar]', payload.avatar)
      let arr = snakecaseKeys(sendProfileLinks)
      arr = arr.map(obj => {
        const {_destroy, ...rest} = obj
        return {_destroy: obj.destroy, ...rest}
      })
      formData.append('expert[profile_links_attributes]', JSON.stringify(arr))
      axios
        .post('/api/experts', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then(
          ({ data }) => {
            const expert = data.data.attributes
            this.experts.unshift(expert)
            this.dialog = false
            const payload = {
              status: 200,
              message: '監修者を追加しました'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.errors
                ? error.response.data.errors.toString()
                : '空白です'
            }
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
    }
  }
}
</script>

<style scoped>
th,
td {
  vertical-align: middle;
}
</style>
